/**
 * Как машина зовётся на экране и как её находит поиск.
 *
 * Главным в списке стоит госномер: это то, чем машину зовут в работе и что
 * написано на ней самой. Имя из Traccar остаётся запасным — у машины, которую
 * успели завести до появления паспорта, номера просто нет.
 *
 * Паспорт (госномер, марка, модель) лежит в атрибутах устройства, а не в поле
 * model: там стоит модель трекера FMC150, и от неё зависит, разберёт ли Traccar
 * данные с шины.
 */

import type { Device } from './api'
import { BRAND_NAMES, type ArtId } from './brandArt'

export type Passport = {
  plate: string | null
  brand: ArtId | null
  model: string | null
}

export function passportOf(device: Device): Passport {
  const a = device.attributes ?? {}
  const brand = typeof a.brand === 'string' && a.brand in BRAND_NAMES ? (a.brand as ArtId) : null
  return {
    plate: text(a.plate),
    brand,
    model: text(a.vehicleModel),
  }
}

function text(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null
}

/** Что показать крупно: госномер, а если его нет — имя из Traccar. */
export function title(device: Device): string {
  return passportOf(device).plate ?? device.name
}

/** Марка и модель одной строкой: «Mercedes-Benz Sprinter». Нечего — null. */
export function describe(device: Device): string | null {
  const { brand, model } = passportOf(device)
  const parts = [brand ? BRAND_NAMES[brand] : null, model].filter(Boolean)
  return parts.length ? parts.join(' ') : null
}

/**
 * Подходит ли машина под запрос.
 *
 * Ищется по всему сразу: номеру, марке, модели и имени. Человек набирает то,
 * что помнит, — «1014», «sprinter», «фиат», — и не должен угадывать, по какому
 * полю тут принято искать.
 *
 * Номер сравнивается без пробелов и дефисов: на машине он написан «B-KEP 1014»,
 * а набирают его как придётся.
 */
export function matches(device: Device, query: string): boolean {
  const needle = query.trim().toLowerCase()
  if (!needle) return true

  const { plate, brand, model } = passportOf(device)
  const haystack = [device.name, plate, model, brand ? BRAND_NAMES[brand] : null, brand]
    .filter(Boolean)
    .map((value) => (value as string).toLowerCase())

  if (haystack.some((value) => value.includes(needle))) return true

  // Номер отдельно — без разделителей с обеих сторон.
  const bare = (value: string) => value.replace(/[\s-]/g, '')
  return plate !== null && bare(plate.toLowerCase()).includes(bare(needle))
}
